import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { isAddress } from "viem";
import { Footer } from "../components/Footer";
import { Nav } from "../components/Nav";
import {
  preferredReviewerAccount,
  X402ReviewerIdentities,
} from "../components/x402/X402ReviewerIdentities";
import { X402ReviewerReviewList } from "../components/x402/X402ReviewerReviewList";
import { shortAddr } from "../lib/api";
import {
  fetchX402ReviewerReviews,
  type X402ReviewerReviewsResponse,
} from "../lib/x402Discovery";
import { fetchPublicX402ProfileByWallet, toBaseAccountId } from "../lib/x402Profile";

export function X402ReviewerPage() {
  const { wallet = "" } = useParams();
  const [data, setData] = useState<X402ReviewerReviewsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [publicProfileId, setPublicProfileId] = useState<string | null>(null);
  const [profileLookup, setProfileLookup] = useState<"idle" | "loading" | "available" | "unavailable">("idle");

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    setLoading(true);
    if (!isAddress(wallet)) {
      setError("This is not a valid EVM wallet address.");
      setLoading(false);
      return () => { cancelled = true; };
    }
    fetchX402ReviewerReviews(wallet, 25)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((cause) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : String(cause));
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [wallet]);

  useEffect(() => {
    let cancelled = false;
    setPublicProfileId(null);
    setProfileLookup("idle");
    if (!isAddress(wallet)) return () => { cancelled = true; };
    setProfileLookup("loading");
    fetchPublicX402ProfileByWallet(toBaseAccountId(wallet))
      .then((result) => {
        if (!cancelled) {
          setPublicProfileId(result.profile.id);
          setProfileLookup("available");
        }
      })
      .catch(() => {
        if (!cancelled) setProfileLookup("unavailable");
      });
    return () => { cancelled = true; };
  }, [wallet]);

  const account = data ? preferredReviewerAccount(data.reviewer.accounts) : undefined;
  const label = account?.handle ?? (isAddress(wallet) ? shortAddr(wallet) : "Reviewer");

  async function loadMore() {
    if (!data || loadingMore || !data.pagination.hasMore) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const next = await fetchX402ReviewerReviews(wallet, 25, data.items.length);
      setData({ ...next, items: [...data.items, ...next.items] });
    } catch (cause) {
      setMoreError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <>
      <Nav current="x402" />
      <main className="overflow-x-clip">
        <section className="px-6 md:px-10 pt-12 md:pt-20 max-w-6xl mx-auto">
          <Link to="/x402" className="btn-mono text-xs text-muted hover:text-ink">← Recognized endpoints</Link>
          <header className="mt-7 max-w-4xl animate-fade-up">
            <p className="btn-mono text-xs uppercase tracking-[0.18em] text-muted">x402 reviewer</p>
            <h1 className="mt-3 text-4xl md:text-6xl font-display tracking-tight leading-[0.98]">
              Reviews by {label}
            </h1>
            <CopyAddress address={wallet} />
            <div className="mt-4 min-h-8 flex items-center">
              {profileLookup === "available" && publicProfileId ? (
                <Link className="pill pill-olive btn-mono text-xs hover:underline" to={`/x402/profiles/${publicProfileId}`}>
                  View public linked-wallet profile →
                </Link>
              ) : profileLookup === "loading" ? (
                <span className="btn-mono text-xs text-muted">Checking for a public linked-wallet profile…</span>
              ) : profileLookup === "unavailable" ? (
                <span className="text-xs text-muted">No public linked-wallet profile was returned.</span>
              ) : null}
            </div>
            {data && (
              <X402ReviewerIdentities
                accounts={data.reviewer.accounts}
                resolution={data.identityResolution}
              />
            )}
          </header>
        </section>

        <section className="px-6 md:px-10 mt-10 md:mt-12 max-w-6xl mx-auto">
          {loading && <Panel title="Reading this wallet’s reviews…" />}
          {!loading && error && <Panel title="Could not read this reviewer." detail={error} />}
          {!loading && data && data.items.length === 0 && (
            <Panel title="No x402 reviews from this wallet yet." />
          )}
          {!loading && data && data.items.length > 0 && (
            <>
              <X402ReviewerReviewList items={data.items} />
              {data.pagination.hasMore && (
                <button className="btn btn-ghost mt-5" type="button" disabled={loadingMore} onClick={() => void loadMore()}>
                  {loadingMore ? "Loading…" : "Load more reviews"}
                </button>
              )}
              {moreError && <p className="mt-3 text-sm text-muted">Could not load more reviews: {moreError}</p>}
            </>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}

function CopyAddress({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(address);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }}
      className="mt-5 inline-flex max-w-full flex-wrap items-center gap-2 btn-mono text-sm text-muted hover:text-ink"
      title="Copy wallet address"
    >
      <span className="text-[10px] uppercase tracking-[0.14em]">Reviewing wallet</span>
      <span className="break-all text-left text-ink">{address}</span>
      <span className="shrink-0 text-olive-deep">{copied ? "copied ✓" : "copy"}</span>
    </button>
  );
}

function Panel({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="border-y border-faint py-12 text-center">
      <p className="font-display text-2xl">{title}</p>
      {detail && <p className="mt-2 text-sm text-muted">{detail}</p>}
    </div>
  );
}
